import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrlWithEnv } from '../utils/imageUrl';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  TrophyIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import apiService from '../services/apiService';

const StandingsManagement = () => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStandings();
  }, []);

  const loadStandings = async () => {
    try {
      setLoading(true);
      const standingsData = await apiService.getStandings();
      setStandings(standingsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading standings:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ნამდვილად გსურთ ამ გუნდის წაშლა?')) {
      try {
        await apiService.deleteStanding(id);
        loadStandings();
      } catch (error) {
        console.error('Error deleting standing:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="სტენდინგების მართვა"
        subtitle="მართეთ სატურნირო ცხრილი"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/standings/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          ახალი გუნდი
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {standings.length === 0 ? (
            <div className="text-center py-12">
              <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">სტენდინგები არ არის</h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">დაიწყეთ ახალი გუნდის დამატებით</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/standings/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  ახალი გუნდი
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ადგ.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      კლუბი
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      თამაში
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      მოგება
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ფრე
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      წაგება
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      გატანილი
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      მიღებული
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ±
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ქულა
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      მოქმედებები
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {standings.map((standing, index) => (
                    <tr key={standing.id} className={standing.isCurrentTeam ? 'bg-primary-50' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <img className="h-8 w-8 rounded-full" src={getImageUrlWithEnv(standing.logo)} alt={standing.name} />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 font-georgian">
                              {standing.name}
                              {standing.isCurrentTeam && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                  ჩვენი გუნდი
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.played}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.won}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.drawn}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.lost}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.goalsFor}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian text-center">
                        {standing.goalsAgainst}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium font-georgian text-center">
                        <span className={standing.goalsFor - standing.goalsAgainst >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {standing.goalsFor - standing.goalsAgainst >= 0 ? '+' : ''}{standing.goalsFor - standing.goalsAgainst}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 font-georgian text-center">
                        {standing.points}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/_panel-7c39f8/standings/${standing.id}/edit`)}
                            className="text-primary-600 hover:text-primary-900 font-georgian"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(standing.id)}
                            className="text-red-600 hover:text-red-900 font-georgian"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandingsManagement;
